/**
 * calendarData.js
 *
 * Derives season-calendar data from bookings and members.
 * Computes per-day utilization, collective activity, and booking detail
 * for the calendar heatmap + detail panel.
 *
 * All data is derived — no new Firestore collections.
 */

import { resolveInstructorCategory } from './instructorCategory';
import { eligibleForActivity, ACTIVITY } from './activityModel';
import { isClientBooking, isLiveBooking } from './scheduleModel';
// Pass 83B.2 — the calendar aggregation is timezone-parameterized: the caller supplies the operating IANA
// zone (My Calendar passes the selected org's zone), and day bucketing + booking-detail time labels use the
// generic zonedDateKey / zonedTimeLabel. The no-tz fallback is LEGACY_OPS_TIMEZONE (Asia/Tokyo) so existing
// SAS callers are byte-for-byte unchanged; NEW callers must pass their zone (never a silent browser default).
import { zonedDateKey, zonedTimeLabel, LEGACY_OPS_TIMEZONE } from './opsTime';

// Pass 59B — a schedule-grid operational HOLD (provisional instructor commitment, NOT a client lesson).
// A STANDALONE hold blocks availability and is shown as a hold; a COVERED hold defers to the real client
// booking (the booking already shows it) and is NOT rendered/counted separately (no double count).
// Pass 81 — the SAS-School schedule-supply count must stay sourced from the SAS-School grid ONLY (§15/§43). A
// SEPARATE source (e.g. Collective, externalSource 'sas_collective_schedule') also produces `activityType:'client_hold'`
// holds, so the legacy `activityType==='client_hold'` fallback is admitted ONLY when no (other) source is stamped —
// never for a hold explicitly belonging to a different source. This keeps SAS supply (16) invariant to Collective.
const isGridHold = (b = {}) => b.externalSource === 'sas_schedule_grid' || (b.activityType === 'client_hold' && !b.externalSource);
const isStandaloneHold = (b = {}) => isGridHold(b) && b.blocksAvailability !== false && b.reconciliationState !== 'covered_by_booking';
// Pass 58F5/59B — an RDO / day-off entry (staff time-off, NOT a client lesson and NOT a grid hold).
// Distinguished by activityType now that grid holds are ALSO scheduleEntryType:'blocked'.
const isRdoEntry = (b = {}) => b.activityType === 'timeoff' || b.isRdoRow === true;

// ── Date helpers ────────────────────────────────────────────────────────────

function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}


// ── Build calendar data from bookings ───────────────────────────────────────

/**
 * Build utilization data for every day in the given month.
 *
 * @param {Object} options
 * @param {Array} options.bookings - normalised booking objects
 * @param {Array} options.members - organization member objects
 * @param {number} options.year
 * @param {number} options.month - 0-indexed
 * @returns {{ utilByDate, collectiveDates, bookingsByDate, totalSlots, monthStats }}
 */
export function buildCalendarMonth({ bookings = [], members = [], year, month, seasonId = '', timeZone = LEGACY_OPS_TIMEZONE }) {
  // Pass 83B.2 — day bucketing uses the supplied operating zone (SAS → Asia/Tokyo via the fallback; a
  // non-JST org sees its own days), so the month grid, utilisation, and month statistics share ONE tz model.
  const tz = timeZone || LEGACY_OPS_TIMEZONE;
  const keyOf = (d) => zonedDateKey(d, tz);
  // §8 category is resolved for the SELECTED season when one is given (never "latest season").
  const catOpts = seasonId ? { season: seasonId } : {};
  // §2 lesson-utilisation capacity = teaching-capable staff for the season (drivers excluded).
  const instructors = eligibleForActivity(members, ACTIVITY.LESSON, seasonId);
  const eligibleIds = new Set(instructors.map(i => i.userId || i.id).filter(Boolean));
  // Pass 46F: canonical category — unknown stays 'unclassified', never silently internal.
  const internalInstructors = instructors.filter(i => resolveInstructorCategory(i, catOpts) === 'school');
  const collectiveInstructors = instructors.filter(i => resolveInstructorCategory(i, catOpts) === 'collective');

  // Total slots (utilisation denominator). Pass 59B2 — when the schedule grid is connected, the denominator
  // is the source-backed SCHEDULE SUPPLY (instructors actually represented in the grid), NOT the full
  // teaching roster — so availability/utilisation never mixes a 17-person roster with a 16-person source
  // population. Falls back to teaching-capable staff when no grid holds are present (backward compatible).
  // NOTE: scheduleSupply is filled in the bookings loop below; totalSlots is finalised after it.

  // Group bookings by date
  const bookingsByDate = {};
  const utilByDate = {};
  // Pass 58F5 — REAL per-day booking DEMAND so the month calendar shows where the bookings are (a
  // utilisation view alone made a full schedule look blank when one instructor took many lessons or
  // demand was unassigned). These are counts of live records, NOT capacity.
  const lessonsByDate = {};     // live CLIENT lessons that day (assigned + unassigned)
  const unassignedByDate = {};  // live client lessons with no instructor
  const rdoByDate = {};         // RDO / day-off / unavailability entries
  const holdsByDate = {};       // Pass 59B — standalone SAS operational holds (provisional commitments)
  const holdInst = {};          // per-day unique held-instructor ids (occupancy, not lesson capacity)
  // Pass 59B2 — the AUTHORITATIVE SCHEDULE SUPPLY = instructors actually represented in the connected grid
  // source (they hold >=1 grid hold this season, standalone OR covered). This is the operational scheduling
  // population (16), distinct from the full teaching roster (17 — a teacher with no grid block is NOT counted
  // here, and is NEVER inferred "available"). Derived mechanically; becomes 17 automatically if that teacher
  // gains a grid block. It is the correct denominator for schedule capacity/utilisation.
  const scheduleSupply = new Set();
  // §2 per-day set of unique ELIGIBLE ASSIGNED lesson-staff ids — the ONLY thing that counts as
  // used lesson capacity. Unassigned demand and non-eligible/unknown instructors are excluded.
  const utilInst = {};
  const collectiveDates = new Set();
  const bump = (map, key) => { map[key] = (map[key] || 0) + 1; };

  for (const b of bookings) {
    // Pass 58F5 — never count retired/superseded or cancelled records as current activity.
    if (!isLiveBooking(b)) continue;
    const start = toDate(b.startAt);
    const key = start ? keyOf(start) : (b.serviceDate || null);
    if (!key) continue;

    // Pass 59B — schedule-grid holds: a STANDALONE hold is a provisional occupancy (bucket + held-instructor
    // set), a COVERED hold is already represented by its real client booking → neither counted nor rendered
    // here (no double count). Grid holds are NEVER lessons and NEVER RDO.
    if (isGridHold(b)) {
      // Pass 81E — SAS-School SUPPLY counts only SCHOOL-supply grid holds. A blended-grid ASSIGNMENT-EVIDENCE hold (a
      // Collective human fulfilling an SAS booking, gridSupplyScope:'assignment_evidence') blocks availability + is
      // assignable but is NOT SAS-School supply, so it never inflates the connected-source supply count (§8). A legacy
      // hold with no scope flag defaults to school supply (byte-for-byte — the live 16 is unchanged).
      if (b.instructorId && b.gridSupplyScope !== 'assignment_evidence') scheduleSupply.add(b.instructorId);
      if (isStandaloneHold(b)) { bump(holdsByDate, key); if (b.instructorId) (holdInst[key] = holdInst[key] || new Set()).add(b.instructorId); }
      continue;
    }
    // RDO / unavailability is a distinct indicator — bucket it and skip the lesson maths.
    if (isRdoEntry(b)) { bump(rdoByDate, key); continue; }
    // §1 the month heatmap is a LESSON view — other non-client work (driving/admin/training) is
    // shown in Day/Week, not counted here.
    if (!isClientBooking(b)) continue;
    if (!start) continue;

    bump(lessonsByDate, key);
    if (!b.instructorId) bump(unassignedByDate, key);
    if (!bookingsByDate[key]) bookingsByDate[key] = { sas: [], collective: [] };

    const member = members.find(m => m.userId === b.instructorId);
    const category = resolveInstructorCategory(member || {}, catOpts);
    const instName = member?.displayName || member?.name || b.instructorId || 'Unknown';

    // Build chip type from booking data
    let chipType = 'fd';
    const dur = b.serviceDurationSnapshot;
    if (dur && dur <= 210) chipType = 'hd';
    if (b._seedMeta?.premium || b.serviceType?.includes('premium')) chipType = 'prem';
    if (b.assignmentSource === 'collective_pull_in') chipType = 'col';

    const detail = {
      inst: instName,
      detail: buildBookingDetail(b, tz),
      type: category === 'collective' ? 'col' : chipType,
      bookingId: b.id,
    };

    if (category === 'collective') {
      bookingsByDate[key].collective.push(detail);
      collectiveDates.add(key);
    } else {
      bookingsByDate[key].sas.push(detail);
    }

    // §2 count toward utilisation ONLY when assigned to an eligible lesson-staff member.
    if (b.instructorId && eligibleIds.has(b.instructorId)) {
      (utilInst[key] = utilInst[key] || new Set()).add(b.instructorId);
    }
  }

  // Compute utilization per date = unique eligible ASSIGNED lesson staff that day.
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (utilInst[key]) utilByDate[key] = utilInst[key].size;
  }

  // Pass 59B2 — finalise the utilisation denominator: source-backed schedule supply when the grid is
  // connected, else teaching-capable staff (backward compatible / test-safe when no holds exist).
  const scheduleSupplyCount = scheduleSupply.size;
  const totalSlots = scheduleSupplyCount || instructors.length || 1;

  // Month stats
  const monthStats = computeMonthStats(utilByDate, totalSlots, year, month);

  // Pass 59B — per-day count of unique held (occupied-by-hold) instructors.
  const heldInstByDate = {};
  for (const k of Object.keys(holdInst)) heldInstByDate[k] = holdInst[k].size;

  return {
    utilByDate,
    collectiveDates,
    bookingsByDate,
    lessonsByDate,
    unassignedByDate,
    rdoByDate,
    holdsByDate,
    heldInstByDate,
    totalSlots,
    scheduleSupplyCount,     // Pass 59B2 — instructors represented in the connected schedule source (16)
    scheduleSupplyIds: Array.from(scheduleSupply),
    monthStats,
    internalCount: internalInstructors.length,
    collectiveCount: collectiveInstructors.length,
  };
}

// ── Month stats ─────────────────────────────────────────────────────────────

function computeMonthStats(utilByDate, totalSlots, year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let bookedDays = 0;
  let totalBooked = 0;
  let peakBooked = 0;
  let peakDay = null;
  let busyDays = 0;

  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const b = utilByDate[key] || 0;
    if (b > 0) {
      bookedDays++;
      totalBooked += b;
      if (b > peakBooked) { peakBooked = b; peakDay = d; }
      if (b / totalSlots >= 0.6) busyDays++;
    }
  }

  const avg = bookedDays > 0 ? Math.round(totalBooked / bookedDays / totalSlots * 100) : 0;

  return {
    avg,
    peakBooked,
    peakPct: totalSlots > 0 ? Math.round(peakBooked / totalSlots * 100) : 0,
    peakDay,
    busyDays,
    bookedDays,
    totalDays: daysInMonth,
  };
}

// ── Build booking detail string ─────────────────────────────────────────────

function buildBookingDetail(booking, timeZone = LEGACY_OPS_TIMEZONE) {
  const parts = [];

  // Duration type
  const dur = booking.serviceDurationSnapshot;
  if (dur && dur >= 360) parts.push('FD');
  else if (dur && dur >= 150) parts.push('HD');

  // Premium
  if (booking._seedMeta?.premium || booking.serviceType?.includes('premium')) {
    parts.push('Prem');
  }

  // Discipline
  if (booking.discipline === 'ski') parts.push('Ski');
  else if (booking.discipline === 'snowboard') parts.push('SB');
  else if (booking.discipline === 'dual') parts.push('Dual');
  else if (booking.discipline === 'guided_tour') parts.push('Guide');

  // Client name
  if (booking.requesterName) {
    parts.push('—');
    parts.push(booking.requesterName);
  }

  // Location
  if (booking.locationLabel) {
    parts.push('·');
    parts.push(booking.locationLabel);
  }

  // Time — Pass 83B.2: wall-clock in the supplied operating zone (not hardcoded JST).
  const start = toDate(booking.startAt);
  if (start) {
    const label = zonedTimeLabel(start, timeZone || LEGACY_OPS_TIMEZONE); // 'HH:MM' in the org zone
    if (label && !label.startsWith('00:')) parts.push(`· ${label}`);
  }

  return parts.join(' ') || booking.serviceNameSnapshot || 'Booking';
}

// ── Format helpers (exported for the component) ─────────────────────────────

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun',
                      'Jul','Aug','Sep','Oct','Nov','Dec'];
const DOW_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export function monthLabel(year, month) {
  return `${MONTHS[month]} ${year}`;
}

export function shortMonthLabel(month) {
  return MONTHS_SHORT[month];
}

export function dayLabel(dateStr) {
  // §5 dateStr is a JST 'YYYY-MM-DD' key; anchor at JST noon so the day-of-week is stable
  // regardless of the operator's locale, and take day/month/year from the key itself.
  const [y, m, day] = String(dateStr).split('-').map(Number);
  const noonJst = new Date(`${dateStr}T12:00:00+09:00`);
  const dow = Number.isNaN(noonJst.getTime())
    ? DOW_NAMES[0]
    : new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'Asia/Tokyo' }).format(noonJst);
  return `${dow} ${day} ${MONTHS_SHORT[(m || 1) - 1]} ${y}`;
}

export function utilBarClass(pct) {
  if (pct === 0) return 'none';
  if (pct < 25) return 'low';
  if (pct < 60) return 'mid';
  if (pct < 85) return 'high';
  return 'peak';
}
